import { GoogleGenAI } from "@google/genai";
import { ReferenceAsset, ImageResolution, AspectRatio, AppSettings } from "../types";

/**
 * Maps standard Gemini model names to Grsai equivalents for Nano Banana series.
 */
const mapGrsaiModel = (standardModel: string, resolution: ImageResolution): string => {
  if (standardModel === 'gemini-2.5-flash-image') {
    return 'nano-banana-fast';
  }
  if (standardModel === 'gemini-3-pro-image-preview') {
    if (resolution === '4K') return 'nano-banana-pro-4k-vip';
    return 'nano-banana-pro';
  }
  return standardModel;
};

/**
 * SiliconFlow specific model mapping.
 */
const mapSiliconFlowModel = (standardModel: string): string => {
  if (standardModel === 'gemini-3-pro-image-preview') {
    return 'Qwen/Qwen-Image-Edit-2509';
  }
  return 'Qwen/Qwen-Image';
};

const mapSiliconFlowSize = (ratio: AspectRatio): string => {
  switch (ratio) {
    case '1:1': return '1024x1024';
    case '16:9': return '1024x576';
    case '9:16': return '576x1024';
    case '4:3': return '1024x768';
    case '3:4': return '768x1024';
    default: return '1024x1024';
  }
};

/**
 * Grsai API implementation using native /v1/draw/nano-banana endpoint.
 * This ensures parameters like aspectRatio and imageSize are respected.
 */
const callGrsaiApi = async (
  prompt: string,
  references: ReferenceAsset[],
  resolution: ImageResolution,
  aspectRatio: AspectRatio,
  settings: AppSettings
): Promise<{ base64: string; mimeType: string }> => {
  const model = mapGrsaiModel(settings.imageModel, resolution);
  const host = settings.grsaiHost.replace(/\/$/, "");
  const url = `${host}/v1/draw/nano-banana`;
  const apiKey = settings.grsaiApiKey || process.env.API_KEY;

  const payload = {
    model: model,
    prompt: prompt,
    aspectRatio: aspectRatio,
    imageSize: resolution,
    // Pass references as base64 images
    urls: references.map(ref => `data:${ref.mimeType};base64,${ref.base64}`),
    shutProgress: true
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Grsai API Error (${response.status}): ${errorText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error("Failed to read stream from Grsai");

  const decoder = new TextDecoder();
  let resultUrl = "";
  let buffer = "";

  // Helper to process a single line of JSON/SSE data
  const processLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed === 'data: [DONE]') return;

    // Handle SSE format: strip "data: " prefix if present
    let jsonStr = trimmed;
    if (jsonStr.startsWith('data:')) {
      jsonStr = jsonStr.slice(5).trim();
    }

    try {
      const data = JSON.parse(jsonStr);
      if (data.status === 'succeeded' && data.results?.[0]?.url) {
        resultUrl = data.results[0].url;
      } else if (data.status === 'failed') {
        // Throw a specific error that we can catch and re-throw to break the loop
        throw new Error("GRSAI_FAIL::" + (data.error || data.failure_reason || "Grsai 任务失败"));
      }
    } catch (e: any) {
      // If it's our explicit failure error, propagate it
      if (e.message?.startsWith("GRSAI_FAIL::")) {
        throw new Error(e.message.split("::")[1]);
      }
      // Otherwise ignore JSON parse errors for incomplete lines
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      if (done) {
        // Process any remaining buffer content when stream ends
        if (buffer.trim()) {
          processLine(buffer);
        }
        break;
      }

      const lines = buffer.split('\n');
      // Keep the last part in buffer as it might be incomplete
      buffer = lines.pop() || "";

      for (const line of lines) {
        processLine(line);
        if (resultUrl) break;
      }
      if (resultUrl) break;
    }
  } catch (err) {
    throw err;
  } finally {
    reader.releaseLock();
  }

  if (!resultUrl) {
    throw new Error("Grsai API 响应中未找到图片 URL");
  }

  // Compatibility: Handle if URL is actually a Data URI
  if (resultUrl.startsWith('data:')) {
    const parts = resultUrl.split(',');
    if (parts.length > 1) {
      const mimeMatch = resultUrl.match(/data:([^;]+);/);
      return {
        base64: parts[1],
        mimeType: mimeMatch ? mimeMatch[1] : 'image/png'
      };
    }
  }

  // Compatibility: Handle if result is raw base64 (not a standard URL)
  if (!resultUrl.startsWith('http')) {
    return {
      base64: resultUrl,
      mimeType: 'image/png' // Assume PNG for raw base64
    };
  }

  // Standard URL Fetch
  const imgRes = await fetch(resultUrl);
  const blob = await imgRes.blob();
  const readerFr = new FileReader();
  const base64Promise = new Promise<string>((resolve) => {
    readerFr.onloadend = () => {
      const result = readerFr.result as string;
      resolve(result.split(',')[1]);
    };
    readerFr.readAsDataURL(blob);
  });

  return {
    base64: await base64Promise,
    mimeType: blob.type || 'image/png'
  };
};

export const generateImage = async (
  prompt: string,
  references: ReferenceAsset[],
  resolution: ImageResolution,
  aspectRatio: AspectRatio,
  settings: AppSettings
): Promise<{ base64: string; mimeType: string }> => {

  if (settings.apiProvider === 'grsai') {
    return callGrsaiApi(prompt, references, resolution, aspectRatio, settings);
  }

  if (settings.apiProvider === 'siliconflow') {
    const model = mapSiliconFlowModel(settings.imageModel);
    const url = 'https://api.siliconflow.cn/v1/images/generations';
    const apiKey = settings.siliconflowApiKey || process.env.API_KEY;

    const payload: any = {
      model: model,
      prompt: prompt,
      image_size: mapSiliconFlowSize(aspectRatio),
      batch_size: 1
    };
    if (references.length > 0) payload.image = `data:${references[0].mimeType};base64,${references[0].base64}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error(await response.text());
    const data = await response.json();
    const imgUrl = data.data?.[0]?.url;
    const imgRes = await fetch(imgUrl);
    const blob = await imgRes.blob();
    const reader = new FileReader();
    const base64Promise = new Promise<string>(r => { reader.onloadend = () => r((reader.result as string).split(',')[1]); reader.readAsDataURL(blob); });
    return { base64: await base64Promise, mimeType: blob.type };
  }

  // Google 官方实现
  const apiKey = settings.googleApiKey || process.env.API_KEY;
  // Use custom Base URL if provided, otherwise the SDK defaults to official
  const baseUrl = settings.googleBaseUrl && settings.googleBaseUrl.trim() !== '' ? settings.googleBaseUrl : undefined;

  const ai = new GoogleGenAI({ apiKey, httpOptions: { baseUrl } });

  let modelName = settings.imageModel;
  if (resolution === '2K' || resolution === '4K') modelName = 'gemini-3-pro-image-preview';

  const parts: any[] = references.map(ref => ({ inlineData: { data: ref.base64, mimeType: ref.mimeType } }));
  parts.push({ text: prompt });

  const response = await ai.models.generateContent({
    model: modelName,
    contents: { parts },
    config: {
      imageConfig: {
        aspectRatio,
        ...(modelName === 'gemini-3-pro-image-preview' ? { imageSize: resolution } : {})
      }
    }
  });

  if (response.candidates?.[0]?.content?.parts) {
    for (const part of response.candidates[0].content.parts) {
      if (part.inlineData) return { base64: part.inlineData.data, mimeType: part.inlineData.mimeType || 'image/png' };
    }
  }

  throw new Error("模型未返回图片内容");
};

export const editImage = async (
  originalImageBase64: string,
  originalMimeType: string,
  editInstruction: string,
  settings: AppSettings
): Promise<{ base64: string; mimeType: string }> => {
  const apiKey = settings.googleApiKey || process.env.API_KEY;
  const baseUrl = settings.googleBaseUrl && settings.googleBaseUrl.trim() !== '' ? settings.googleBaseUrl : undefined;
  const ai = new GoogleGenAI({ apiKey, httpOptions: { baseUrl } });

  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash-image',
    contents: {
      parts: [
        { inlineData: { data: originalImageBase64, mimeType: originalMimeType } },
        { text: editInstruction },
      ],
    },
  });

  if (response.candidates?.[0]?.content?.parts) {
    for (const part of response.candidates[0].content.parts) {
      if (part.inlineData) return { base64: part.inlineData.data, mimeType: part.inlineData.mimeType || 'image/png' };
    }
  }
  throw new Error("编辑失败");
};

export const matchAssetsToPrompt = async (
  prompt: string,
  assets: ReferenceAsset[],
  settings: AppSettings
): Promise<string[]> => {
  if (assets.length === 0 || !prompt) return [];
  const normalizedPrompt = prompt.toLowerCase();
  return assets
    .filter(asset => {
      if (!asset.keywords) return false;
      const keys = asset.keywords.split('|').map(k => k.trim().toLowerCase()).filter(Boolean);
      return keys.some(k => normalizedPrompt.includes(k));
    })
    .map(a => a.id);
};
